import { IconDotsVertical } from '@tabler/icons-react'
import {
  App,
  Dropdown,
  Form,
  Input,
  Tree,
  TreeDataNode,
  Typography,
} from 'antd'
import { Key } from 'antd/es/table/interface'
import classNames from 'classnames'
import React, {
  PropsWithChildren,
  useCallback,
} from 'react'

import { FunctionKey } from '@/constants/function-key'
import useFormModal from '@/hooks/use-form-modal'
import {
  Group,
  GroupType,
} from '@/model/group'
import { selectMineFunctionKeys } from '@/redux/function-permission-selector'
import {
  createGroup,
  CreateGroupParams,
  removeGroup,
  updateGroup,
  UpdateGroupParams,
} from '@/redux/group'
import { selectGroupTreeInfo } from '@/redux/group-selectors'
import {
  createAppSelector,
  useAppDispatch,
  useAppSelector,
} from '@/redux/utils'

import s from './group-tree.module.css'

const { Item } = Form
const { Paragraph } = Typography

export interface GroupTreeProps extends PropsWithChildren {
  type: GroupType;
  value?: number;
  onChange?: (groupId: number, group?: Group) => void;
  editable?: boolean;
  ellipsis?: boolean;
  maxAncestors?: number;
}

function GroupFields() {
  return (
    <>
      <Item name="id" hidden />
      <Item name="ancestorIds" hidden />
      <Item name="parentId" hidden />
      <Item name="type" hidden />
      <Item
        name="name"
        label="Group Name"
        required
        rules={[{ required: true, max: 100 }]}
      >
        <Input count={{ show: true, max: 100 }} />
      </Item>
    </>
  )
}

const selector = createAppSelector([
  selectMineFunctionKeys,
  (state, type: GroupType) => selectGroupTreeInfo(state, type),
], (mineFunctionKeys, {
  treeDataNodes,
  rootGroupId,
  groups,
}) => ({
  treeDataNodes,
  rootGroupId,
  groups,
  mineFunctionKeys,
}))

export default function GroupTree({
  type,
  value,
  onChange,
  editable,
  maxAncestors,
  ellipsis,
}: GroupTreeProps) {
  const {
    treeDataNodes,
    rootGroupId,
    groups,
    mineFunctionKeys,
  } = useAppSelector((state) => selector(state, type))
  const dispatch = useAppDispatch()
  const { message, modal } = App.useApp()
  const onTreeSelect = useCallback(
    (selectedKeys: Key[]) => {
      if (onChange) {
        onChange(selectedKeys[0] as number, groups.find(({ id }) => id === selectedKeys[0]))
      }
    },
    [groups, onChange],
  )
  const onCreate = useCallback(
    async (values: CreateGroupParams) => {
      await dispatch(createGroup(values)).unwrap()
      void message.success('Create group success')
    },
    [dispatch, message],
  )
  const [createFormModal, openCreateFormModal] = useFormModal({
    onSubmit: onCreate,
    getFields: () => <GroupFields />,
    title: 'Create Group',
  })
  const onUpdate = useCallback(
    async (values: UpdateGroupParams) => {
      await dispatch(updateGroup(values)).unwrap()
      void message.success('Update group success')
    },
    [dispatch, message],
  )
  const [updateFormModal, openUpdateFormModal] = useFormModal({
    onSubmit: onUpdate,
    getFields: () => <GroupFields />,
    title: 'Update Group',
  })
  const realSelectedGroupId = value || rootGroupId
  const titleRender = useCallback(
    (dataNode: TreeDataNode) => {
      const group = groups.find(({ id }) => id === dataNode.key)

      if (!group) {
        return null
      }
      const { id, ancestorIds } = group

      const isRoot = ancestorIds.length === 0

      const dropdownMenuItems = []
      if (mineFunctionKeys.includes(type === GroupType.Creator ? FunctionKey.CreatorsManageAddGroup : FunctionKey.EmployeesManageAddGroup)) {
        dropdownMenuItems.push({
          label: 'Add sub-group',
          key: 'add',
          disabled: maxAncestors !== undefined && ancestorIds.length >= maxAncestors,
          onClick: () =>
            openCreateFormModal({
              ancestorIds: [...ancestorIds, id],
              parentId: id,
              type,
            }),
        })
      }
      if (mineFunctionKeys.includes(type === GroupType.Creator ? FunctionKey.CreatorsManageEditGroup : FunctionKey.EmployeesManageEditGroup)) {
        dropdownMenuItems.push({
          label: 'Edit',
          key: 'edit',
          disabled: isRoot,
          onClick: () => openUpdateFormModal(group),
        })
      }
      if (mineFunctionKeys.includes(type === GroupType.Creator ? FunctionKey.CreatorsManageDeleteGroup : FunctionKey.EmployeesManageDeleteGroup)) {
        dropdownMenuItems.push({
          label: 'Delete',
          key: 'delete',
          danger: true,
          onClick: () => {
            void modal.confirm({
              title: 'Delete Group',
              content: 'Are you sure to delete it?',
              okType: 'danger',
              okText: 'Delete',
              icon: null,
              maskClosable: true,
              onOk: async () => {
                await dispatch(removeGroup(group.id)).unwrap()
                void message.success('Delete group success')
              },
            })
          },
          disabled: isRoot,
        })
      }

      return (
        <div className={s.GroupTreeItem}>
          <Paragraph
            ellipsis={ellipsis}
            className={classNames(s.NodeTitle, {
              [s.selected]: realSelectedGroupId === id,
            })}
          >
            {typeof dataNode.title === 'function'
              ? dataNode.title(dataNode)
              : dataNode.title}
          </Paragraph>
          {editable && dropdownMenuItems.length > 0 && (
            <Dropdown
              trigger={['click']}
              menu={{
                onClick: ({ domEvent }) => domEvent.stopPropagation(),
                items: dropdownMenuItems,
              }}
            >
              <IconDotsVertical
                size={14}
                onClick={(event) => event.stopPropagation()}
              />
            </Dropdown>
          )}
        </div>
      )
    },
    [
      mineFunctionKeys,
      ellipsis,
      editable,
      dispatch,
      type,
      groups,
      maxAncestors,
      message,
      modal,
      openCreateFormModal,
      openUpdateFormModal,
      realSelectedGroupId,
    ],
  )

  return (
    <>
      <Tree
        className={s.GroupTree}
        selectedKeys={realSelectedGroupId ? [realSelectedGroupId] : undefined}
        defaultExpandAll
        onSelect={onTreeSelect}
        treeData={treeDataNodes}
        titleRender={titleRender}
        blockNode
      />
      {editable && createFormModal}
      {editable && updateFormModal}
    </>
  )
}
